/**
 * Access Link viewers can load page content without space access, so the unified query may
 * return valid content alongside space permission errors.
 *
 * Match only the known space-denial errors so we keep usable content without hiding real failures.
 * If the backend wording changes, this safely falls back to the existing error behavior.
 */

const SPACE_DENIAL_SIGNATURES = ['current space settings', 'permitted to view space'];

const isSpaceDenialMessage = (message: string): boolean =>
	SPACE_DENIAL_SIGNATURES.some((signature) => message.includes(signature));

/**
 * `every` is deliberate: if a response carries a space denial *and* something real, the real one
 * must still be handled, so a mixed set returns false.
 */
export function isSpaceDenialError(error?: Error | null): boolean {
	if (!error) {
		return false;
	}

	// A transport failure is never a field-level denial, whatever else came back with it.
	if ((error as { networkError?: unknown }).networkError) {
		return false;
	}

	const graphQLErrors = (error as { graphQLErrors?: ReadonlyArray<{ message?: string }> })
		.graphQLErrors;

	if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
		return graphQLErrors.every((graphQLError) =>
			isSpaceDenialMessage(String(graphQLError?.message ?? graphQLError)),
		);
	}

	// No structured errors to read. Fall back to the message, which for a multi-error Apollo error
	// is the individual messages joined by newlines — so check each line, not the whole blob.
	const message = String(error.message ?? error);
	const lines = message.split('\n').filter((line) => line.trim().length > 0);

	return lines.length > 0 && lines.every(isSpaceDenialMessage);
}
